"use client";

import Link from "next/link";
import Script from "next/script";
import { useRef, useState, useSyncExternalStore } from "react";
import { tracking } from "@/lib/tracking";
import { analyticsEnabled, getAnalyticsPreference, saveAnalyticsPreference, subscribeToAnalyticsPreference } from "@/lib/analytics-preferences";

function AnalyticsScripts() {
  return (
    <>
      <Script id="dbm-google-tag-init" strategy="afterInteractive">
        {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {analytics_storage:'granted',ad_storage:'granted',ad_user_data:'granted',ad_personalization:'granted'});
gtag('js', new Date());
gtag('config', '${tracking.ga4MeasurementId}');
gtag('config', '${tracking.googleAdsId}');
window.dispatchEvent(new Event('dbm-ga4-ready'));`}
      </Script>
      <Script
        id="dbm-google-tag"
        src={`https://www.googletagmanager.com/gtag/js?id=${tracking.ga4MeasurementId}`}
        strategy="afterInteractive"
      />
      <Script id="dbm-google-tag-manager" strategy="afterInteractive">
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f)})(window,document,'script','dataLayer','${tracking.gtmId}');`}
      </Script>
      <Script id="dbm-clarity" strategy="afterInteractive">
        {`(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src='https://www.clarity.ms/tag/'+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,'clarity','script','${tracking.clarityProjectId}');`}
      </Script>
      {/* HubSpot tracking: sets the hubspotutk visitor cookie and records the
          landing page view (with its UTMs / gclid). Lead submissions forward
          that cookie so HubSpot can attribute the contact's original source. */}
      <Script
        id="hs-script-loader"
        src={`https://${tracking.hubspotScriptHost}/${tracking.hubspotPortalId}.js`}
        strategy="afterInteractive"
      />
    </>
  );
}

function PrivacyControls({ enabled, privacySignal }: { enabled: boolean; privacySignal: boolean }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [draftEnabled, setDraftEnabled] = useState(enabled);
  const openSettings = () => {
    setDraftEnabled(enabled);
    dialog.current?.showModal();
  };
  const save = (allow: boolean) => {
    saveAnalyticsPreference(allow ? "accepted" : "declined");
    dialog.current?.close();
    if (allow !== enabled) {
      if (!allow) {
        window.gtag?.("consent", "update", { analytics_storage: "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
        (window as unknown as Record<string, unknown>)[`ga-disable-${tracking.ga4MeasurementId}`] = true;
        // Tells HubSpot to stop tracking this browser even before the reload.
        (window._hsq = window._hsq ?? []).push(["doNotTrack"]);
      }
      // A reload unloads every previously injected provider, including GTM's tags.
      window.location.reload();
    }
  };

  return (
    <>
      <button type="button" className="privacy-settings-trigger" onClick={openSettings}>Privacy settings</button>
      <dialog ref={dialog} className="privacy-settings-dialog" aria-labelledby="privacy-settings-title">
        <div className="privacy-settings-header">
          <h2 id="privacy-settings-title">Your privacy settings</h2>
          <button type="button" className="privacy-close" aria-label="Close privacy settings" onClick={() => dialog.current?.close()}>×</button>
        </div>
        <p>Choose whether we can measure visits, advertising performance and site interactions using Google Analytics, Google Ads, Google Tag Manager, Microsoft Clarity and HubSpot.</p>
        <label className="privacy-tracking-option">
          <span><strong>Analytics and marketing tracking</strong><small>{draftEnabled ? "On" : "Off"} · Applies to all five tools</small></span>
          <input type="checkbox" role="switch" aria-label="Analytics and marketing tracking" checked={draftEnabled} disabled={privacySignal} onChange={(event) => setDraftEnabled(event.target.checked)} />
        </label>
        {privacySignal ? <p className="privacy-signal-note">Tracking is off because your browser sends a privacy opt-out signal.</p> : null}
        <p>Tracking is on by default. You can change it here at any time. Saving a change reloads this page.</p>
        <p>Contact forms and meeting bookings still work with tracking off. <Link href="/privacy-policy">Read our privacy policy</Link>.</p>
        <div className="privacy-settings-actions">
          <button type="button" className="privacy-settings-link" onClick={() => save(false)}>Turn all off</button>
          <button type="button" className="btn btn-accent btn-sm" onClick={() => save(draftEnabled)}>Save preferences</button>
        </div>
      </dialog>
    </>
  );
}

/** Default-on tracking with persistent visitor opt-outs and on-demand settings. */
export function SiteTracking() {
  const preference = useSyncExternalStore(subscribeToAnalyticsPreference, getAnalyticsPreference, () => "loading");
  const enabled = preference === "default" || preference === "accepted";
  const shouldLoadTracking = enabled && analyticsEnabled();
  return (
    <>
      {shouldLoadTracking ? <AnalyticsScripts /> : null}
      {preference !== "loading" ? <PrivacyControls enabled={enabled} privacySignal={preference === "privacy-signal"} /> : null}
    </>
  );
}

export function TrackingNoScript() {
  return null;
}
